import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useCreateCalendar,
  ApiError,
} from '../../hooks/useCalendars';
import { api } from '../../lib/api';
import UpgradeModal from '../UpgradeModal';
import ColorPicker from '../ColorPicker';
import CalendarToggleSwitch from '../CalendarToggleSwitch';
import { TIMEZONES } from '../../lib/calendarFormConstants';

interface Props {
  onClose: () => void;
}

export default function CreateCalendarModal({
  onClose,
}: Props) {
  const navigate = useNavigate();
  const { mutateAsync: createCalendar, isPending } =
    useCreateCalendar();

  const { data: billing } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => api.billing.status(),
  });
  const { data: pricing } = useQuery({
    queryKey: ['billing', 'prices'],
    queryFn: () => api.billing.prices(),
    // Only fetch pricing if user isn't already subscribed
    enabled: billing?.plan.status !== 'active',
  });
  const proPrice = pricing?.prices.find(
    p => p.tier === 'pro'
  );

  const [name, setName] = useState('');
  const [color, setColor] = useState('#3182ce');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'UTC'
  );
  const [description, setDescription] = useState('');
  const [warnOnConflicts, setWarnOnConflicts] =
    useState(false);
  const [isKocal, setIsKocal] = useState(false);
  const [showKocalInfo, setShowKocalInfo] = useState(false);
  const [showConflictInfo, setShowConflictInfo] =
    useState(false);
  const infoTimeoutRef = useRef<NodeJS.Timeout | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [upgradeVariant, setUpgradeVariant] = useState<
    | 'trial'
    | 'past_due'
    | 'expired'
    | 'readonly'
    | 'limit'
    | null
  >(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setUpgradeVariant(null);
    try {
      const { calendar } = await createCalendar({
        name: name.trim(),
        color,
        timezone,
        description: description.trim() || undefined,
        warn_on_conflicts: warnOnConflicts,
        is_kocal: isKocal,
      });
      onClose();
      navigate(`/calendar/${calendar.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        // Map error code to upgrade variant
        const code = err.code;
        if (code === 'trial_expired') {
          setUpgradeVariant('trial');
        } else if (
          code === 'payment_failed' ||
          code === 'subscription_past_due'
        ) {
          setUpgradeVariant('past_due');
        } else if (
          code === 'subscription_expired_readonly' ||
          code === 'tier_limit_readonly'
        ) {
          setUpgradeVariant('readonly');
        } else if (code === 'calendar_limit_reached') {
          setUpgradeVariant('limit');
        } else {
          // Default to expired for subscription_required and other cases
          setUpgradeVariant('expired');
        }
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Something went wrong'
        );
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-900/40"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="paper-card w-full max-w-md p-6 overflow-y-auto max-h-[90dvh]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-xl font-bold text-ink-900">
            New Calendar
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-ink-400 hover:text-ink-700 transition-colors text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {upgradeVariant ? (
          <UpgradeModal
            variant={upgradeVariant}
            onClose={() => setUpgradeVariant(null)}
            onSuccess={async () => {
              // For trial variant, just close and let user try again
              // For other variants, the checkout redirect handles it
              if (upgradeVariant === 'trial') {
                setUpgradeVariant(null);
              }
            }}
          />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            {/* Trial / subscription banner */}
            {billing &&
              billing.plan.status !== 'active' && (
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 space-y-2">
                  {billing.plan.status === 'free' ? (
                    <>
                      <p className="font-mono text-sm text-blue-900 font-semibold">
                        This starts your 90-day free trial
                      </p>
                      <p className="text-xs text-blue-800 leading-relaxed">
                        Creating your first non-personal
                        calendar begins a free 90-day trial.
                        After that,{' '}
                        {proPrice ? (
                          <strong>
                            {proPrice.formatted}/
                            {proPrice.interval}
                          </strong>
                        ) : (
                          <strong>
                            a small monthly fee
                          </strong>
                        )}{' '}
                        keeps full editing access.
                      </p>
                      <p className="text-xs text-blue-800 leading-relaxed">
                        If you don't subscribe, your
                        calendar and its events stay visible
                        — but you won't be able to edit them
                        after the trial ends.
                      </p>
                    </>
                  ) : billing.plan.status === 'trial' ? (
                    <>
                      <p className="font-mono text-sm text-blue-900 font-semibold">
                        You're on the free trial
                      </p>
                      <p className="text-xs text-blue-800 leading-relaxed">
                        {billing.plan.trial_ends_at && (
                          <>
                            Trial ends{' '}
                            <strong>
                              {new Date(
                                billing.plan.trial_ends_at
                              ).toLocaleDateString()}
                            </strong>
                            .{' '}
                          </>
                        )}
                        Subscribe for{' '}
                        {proPrice ? (
                          <strong>
                            {proPrice.formatted}/
                            {proPrice.interval}
                          </strong>
                        ) : (
                          <strong>
                            a small monthly fee
                          </strong>
                        )}{' '}
                        to keep editing after the trial.
                      </p>
                    </>
                  ) : null}
                </div>
              )}

            <div>
              <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                autoFocus
                maxLength={100}
                className="input-field"
                placeholder="Team calendar"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div>
              <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
                Color
              </label>
              <ColorPicker
                value={color}
                onChange={setColor}
              />
            </div>

            <div>
              <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
                Timezone
              </label>
              <select
                className="input-field"
                value={timezone}
                onChange={e => setTimezone(e.target.value)}
              >
                {TIMEZONES.map(tz => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-mono text-xs text-ink-400 mb-1.5 uppercase tracking-widest">
                Description
              </label>
              <textarea
                className="input-field resize-none"
                rows={2}
                maxLength={500}
                placeholder="Optional description"
                value={description}
                onChange={e =>
                  setDescription(e.target.value)
                }
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
                Enable Kocal
              </span>
              <button
                type="button"
                className="text-ink-400 hover:text-ink-600 transition-colors focus:outline-none relative"
                onMouseEnter={() => {
                  if (infoTimeoutRef.current) {
                    clearTimeout(infoTimeoutRef.current);
                  }
                  setShowKocalInfo(true);
                }}
                onMouseLeave={() => {
                  infoTimeoutRef.current = setTimeout(
                    () => setShowKocalInfo(false),
                    150
                  );
                }}
                onClick={() =>
                  setShowKocalInfo(!showKocalInfo)
                }
                onBlur={() => setShowKocalInfo(false)}
                aria-label="Learn about Kocal"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    strokeWidth="2"
                  />
                  <path
                    strokeWidth="2"
                    strokeLinecap="round"
                    d="M12 16v-4M12 8h.01"
                  />
                </svg>
                {showKocalInfo && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-ink-900 text-white text-xs rounded shadow-xl z-50">
                    <p className="leading-relaxed">
                      When enabled, anyone with your
                      calendar link can contribute events
                      from their personal calendar. Events
                      appear on both their calendar and
                      yours. You can mute contributors if
                      needed.
                    </p>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-ink-900 rotate-45 -mt-1" />
                  </div>
                )}
              </button>
              <CalendarToggleSwitch
                checked={isKocal}
                onChange={setIsKocal}
              />
            </label>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <span className="font-mono text-xs text-ink-400 uppercase tracking-widest">
                Warn on conflicts
              </span>
              <button
                type="button"
                className="text-ink-400 hover:text-ink-600 transition-colors focus:outline-none relative"
                onMouseEnter={() => {
                  if (infoTimeoutRef.current) {
                    clearTimeout(infoTimeoutRef.current);
                  }
                  setShowConflictInfo(true);
                }}
                onMouseLeave={() => {
                  infoTimeoutRef.current = setTimeout(
                    () => setShowConflictInfo(false),
                    150
                  );
                }}
                onClick={() =>
                  setShowConflictInfo(!showConflictInfo)
                }
                onBlur={() => setShowConflictInfo(false)}
                aria-label="Learn about conflict warnings"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    strokeWidth="2"
                  />
                  <path
                    strokeWidth="2"
                    strokeLinecap="round"
                    d="M12 16v-4M12 8h.01"
                  />
                </svg>
                {showConflictInfo && (
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-ink-900 text-white text-xs rounded shadow-xl z-50">
                    <p className="leading-relaxed">
                      When enabled, you'll see a warning if
                      new events overlap with existing ones.
                      Conflicts never block saving — they
                      just help you notice over-scheduling.
                      Useful for shared resource calendars
                      such as cars or vacation properties!
                    </p>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-ink-900 rotate-45 -mt-1" />
                  </div>
                )}
              </button>
              <CalendarToggleSwitch
                checked={warnOnConflicts}
                onChange={setWarnOnConflicts}
              />
            </label>

            {error && (
              <p className="font-mono text-xs text-red-600">
                {error}
              </p>
            )}

            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={onClose}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isPending || !name.trim()}
                className="btn-primary"
              >
                {isPending ? 'Creating…' : 'Create'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
